<script setup lang="ts">
import axios from 'axios'

import useWalletTabs from './composables/tabs'

import WalletConfig from './components/WalletConfig.vue'
import WalletEmptyState from './components/WalletEmptyState.vue'

import { Loader2 } from 'lucide-vue-next'

import type { Wallet, WalletErrors, WalletUsageStatistics } from '@/types/wallet'
import WalletStatistics from './components/WalletStatistics.vue'
import { syncWalletDerivedFields } from './walletFields'

const { t } = useI18n()
const { user, permissions, merchantLogo, hasWallet } = useLocalStorage()
const { tabs, currentTab } = useWalletTabs()
const notification = useNotification()

const isLoaded = ref(false)
const isProcessing = ref(false)
const isCreate = ref(false)
const wallet = ref<Wallet | null>(null)
const statistics = ref<WalletUsageStatistics | null>(null)
const defaultWalletModelId = ref(0)
const defaultFields = ref({
  merchantName: user.value.merchant.name,
  iosLogo: merchantLogo.value
})
const errors = ref<WalletErrors>({
  iosPointsTitle: [],
  merchantName: [],
  androidMandatoryTitle: []
})

onMounted(async () => {
  await fetchWallets()
  if (statistics.value?.passes?.active === 0) {
    tabs.value = tabs.value.filter((tab) => tab.id !== 'stats')
    tabs.value[0].current = true
  }
})

const userCanEditWallet = computed(() => permissions.value['reward.update'])

const merchantId = computed(() => user.value.merchant.id)

const fetchWallets = async () => {
  try {
    const { data: response } = await axios.get(`/merchants/${merchantId.value}/wallets`)
    if (response.data.length > 0) {
      wallet.value = response.data[0]
      await fetchWalletStatistics(wallet.value.id)
    }
    isLoaded.value = true
  } catch (error) {
    notification.create('error', {
      text: t('pages.loyaltySupports.wallet.notifications.fetch.error'),
      timeout: 3000
    })
    throw new Error(error)
  }
}

const fetchWalletStatistics = async (walletId: number) => {
  try {
    const { data } = await axios.get(`/merchants/${merchantId.value}/wallets/${walletId}/statistics`)
    statistics.value = data.data
  } catch (error) {
    notification.create('error', {
      text: t('pages.loyaltySupports.wallet.notifications.statistics.error'),
      timeout: 3000
    })
    throw new Error(error)
  }
}

const fetchDefaultWalletModel = async () => {
  try {
    const { data: response } = await axios.get('/wallets/models')
    const defaultModel = response.data.find((model) => model.isDefault)
    if (defaultModel) {
      defaultWalletModelId.value = defaultModel.id
    }
  } catch (error) {
    throw new Error(error)
  }
}

const fetchDefaultWalletModelFields = async () => {
  try {
    const { data: response } = await axios.get(`/wallets/models/${defaultWalletModelId.value}`)
    defaultFields.value = {
      ...Object.fromEntries(response.data.fields.map((field) => [field.name, field.defaultValue])),
      merchantName: defaultFields.value.merchantName,
      iosLogo: defaultFields.value.iosLogo
    }
  } catch (error) {
    throw new Error(error)
  }
}

const isFormValid = () => {
  const mandatoryFields = ['iosPointsTitle', 'merchantName', 'androidMandatoryTitle']

  errors.value = mandatoryFields.reduce(
    (acc, field) => {
      const isFieldEmpty = !wallet.value.fields[field].trim()
      acc[field] = isFieldEmpty ? [t('pages.loyaltySupports.wallet.config.form.required')] : []
      return acc
    },
    {
      iosPointsTitle: [],
      merchantName: [],
      androidMandatoryTitle: []
    } as WalletErrors
  )

  const hasErrors = mandatoryFields.some((field) => errors.value[field].length > 0)

  return !hasErrors
}

const saveWallet = async () => {
  if (isProcessing.value) {
    return
  }

  if (!isFormValid()) {
    notification.create('error', {
      text: t('pages.loyaltySupports.wallet.notifications.emptyTexts'),
      timeout: 3000
    })
    return
  }

  if (wallet.value) {
    isProcessing.value = true
    const successMessage = isCreate.value
      ? t('pages.loyaltySupports.wallet.notifications.create.success')
      : t('pages.loyaltySupports.wallet.notifications.edit.success')

    try {
      const method = isCreate.value ? 'post' : 'patch'
      const url = isCreate.value
        ? `/merchants/${merchantId.value}/wallets`
        : `/merchants/${merchantId.value}/wallets/${wallet.value.id}`

      syncWalletDerivedFields(wallet.value.fields)
      const { data: response } = await axios[method](url, {
        ...wallet.value
      })
      wallet.value = response.data
      notification.create('success', {
        text: successMessage,
        timeout: 3000
      })
      isCreate.value = false
    } catch {
      notification.create('error', {
        text: t('pages.loyaltySupports.wallet.notifications.edit.error'),
        timeout: 3000
      })
    } finally {
      isProcessing.value = false
      hasWallet.value = true
    }
  }
}

const initNewWallet = async () => {
  await fetchDefaultWalletModel()
  await fetchDefaultWalletModelFields()

  isCreate.value = true
  wallet.value = {
    walletModelId: defaultWalletModelId.value,
    countryId: user.value.countryId,
    fields: { ...defaultFields.value }
  }
}
</script>

<template>
  <div>
    <div v-if="isLoaded">
      <WalletEmptyState v-if="!wallet" @on-create-wallet="initNewWallet()" />

      <div v-else>
        <TheListHeader :title="t('pages.loyaltySupports.wallet.title')">
          <template #actions>
            <Button
              v-if="currentTab === 'config' && userCanEditWallet"
              variant="default-primary"
              :disabled="isProcessing"
              @click.prevent="saveWallet()"
            >
              <Loader2 v-if="isProcessing" class="animate-spin" />
              {{ t('pages.loyaltySupports.wallet.cta.save') }}
            </Button>
          </template>
        </TheListHeader>

        <div class="mb-4 px-6 sm:px-12">
          <BaseTab v-model="currentTab" :tabs="tabs" />
        </div>
        <div class="px-6 pb-6 sm:px-12 sm:pb-12">
          <WalletConfig
            v-if="currentTab === 'config'"
            :edit-mode="userCanEditWallet"
            :wallet="wallet"
            :errors="errors"
          />
          <WalletStatistics v-else-if="currentTab === 'stats'" :statistics="statistics" />
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>
